import {
  AloftArea,
  AloftPrice,
  CraftsmanAdjustmentProps,
  ORDER_FEATURES,
  getCraftsmanAdjustment,
  isDefined,
  isUndefined,
} from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import { selectAdjustmentSettings, selectCompSequence } from '../orderSelectors.ts';
import { selectImprovementsEntities } from '../propertyDataSelectors/improvementsSelectors.ts';
import { selectManualAdjustmentsEntities } from '../propertyDataSelectors/manualAdjustmentsSelectors.ts';
import { selectPropertyIdentificationEntities } from '../propertyDataSelectors/propertyIdentificationSelectors.ts';
import { selectDepreciationRatiosForFeature } from '../selectDepreciationRatioForFeature.ts';
import { createPriceSelector } from '../utils/createPriceSelector.ts';
import { selectAdjustmentProps } from './selectAdjustmentProps.ts';
import { applyRoundingAndThresholds } from './utils/applyRoundingAndThresholds.ts';
import { getColorFromAdjustment } from './utils/getColorFromAdjustment.ts';
import { getStringValueFromAdjustment } from './utils/getStringValueFromAdjustment.ts';

const calculateShedAdj = (
  adjustmentProps: CraftsmanAdjustmentProps,
  improvements: ReturnType<typeof selectImprovementsEntities>,
  propertyIdentification: ReturnType<typeof selectPropertyIdentificationEntities>,
  depreciationRatios: ReturnType<typeof selectDepreciationRatiosForFeature>,
  compID: string,
) => {
  const subjectImprovements = improvements.subject;
  const compImprovements = improvements[compID];

  const subjectPropertyIdentification = propertyIdentification.subject;
  const compPropertyIdentification = propertyIdentification[compID];

  if (
    isUndefined(subjectImprovements) ||
    isUndefined(compImprovements) ||
    isUndefined(depreciationRatios) ||
    isUndefined(subjectPropertyIdentification) ||
    isUndefined(compPropertyIdentification)
  ) {
    return undefined;
  }

  const depreciationRatio = depreciationRatios.subject;
  const compDepreciationRatio = depreciationRatios[compID];

  if (isUndefined(depreciationRatio) || isUndefined(compDepreciationRatio)) {
    return undefined;
  }

  const { shed } = subjectImprovements;
  const { shed: compShed } = compImprovements;
  const {
    address: { PostalCode: zipCode },
  } = subjectPropertyIdentification;
  const {
    address: { PostalCode: compZipCode },
  } = compPropertyIdentification;

  const adjCalculator = getCraftsmanAdjustment(adjustmentProps);

  if (isUndefined(zipCode) || isUndefined(compZipCode)) {
    return undefined;
  }

  return adjCalculator.getShedAdj({
    subject: {
      hasFeature: ORDER_FEATURES.shed.hasFeature(subjectImprovements),
      sqft: shed?.area ? AloftArea.schema.parse(shed?.area) : undefined,
      quality: shed?.quality,
      zipCode,
    },
    comp: {
      hasFeature: ORDER_FEATURES.shed.hasFeature(compImprovements),
      sqft: compShed?.area ? AloftArea.schema.parse(compShed?.area) : undefined,
      quality: compShed?.quality,
      zipCode: compZipCode,
    },
    depreciationRatio,
    compDepreciationRatio,
  }).adjustment;
};

const selectShedAdj = createPriceSelector(
  [
    selectAdjustmentProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    (state) => selectDepreciationRatiosForFeature(state, 'shed'),
    (state, id: string) => id,
  ],
  calculateShedAdj,
);

export const selectShedAdjs = createSelector(
  [
    selectAdjustmentProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    (state) => selectDepreciationRatiosForFeature(state, 'shed'),
    selectCompSequence,
  ],
  (adjustmentVersion, improvements, propertyIdentification, depreciationRatios, compSequence) =>
    compSequence.reduce((acc: Record<string, AloftPrice>, propID) => {
      const adj = calculateShedAdj(
        adjustmentVersion,
        improvements,
        propertyIdentification,
        depreciationRatios,
        propID,
      );

      if (isDefined(adj)) acc[propID] = adj;

      return acc;
    }, {}),
);

const selectAppliedShedAdj = createPriceSelector(
  [selectShedAdj, selectAdjustmentSettings],
  (adj, adjSettings) => {
    if (isUndefined(adj)) {
      return undefined;
    }

    return applyRoundingAndThresholds(adj, 'shed', adjSettings);
  },
);

export const selectAppliedShedAdjDisplay = createSelector([selectAppliedShedAdj], (adj) => {
  if (isUndefined(adj)) {
    return undefined;
  }

  return {
    value: getStringValueFromAdjustment(adj),
    color: getColorFromAdjustment(adj),
  };
});

export const selectAppliedShedAdjsDisplay = createSelector(
  [selectShedAdjs, selectAdjustmentSettings, selectManualAdjustmentsEntities],
  (adjs, adjSettings, manualAdjustmentEntities) =>
    Object.keys(adjs).reduce((acc: Record<string, { value: string; color: string }>, propID) => {
      const adj = adjs[propID];
      const manualAdjustment = manualAdjustmentEntities[propID]?.shed;
      if (isDefined(manualAdjustment)) {
        const manualAdjustmentPrice = AloftPrice.schema.parse(manualAdjustment);
        acc[propID] = {
          value: `${getStringValueFromAdjustment(manualAdjustmentPrice)}*`,
          color: getColorFromAdjustment(manualAdjustmentPrice),
        };
      } else if (isDefined(adj)) {
        acc[propID] = {
          value: getStringValueFromAdjustment(
            applyRoundingAndThresholds(adjs[propID], 'shed', adjSettings),
          ),
          color: getColorFromAdjustment(adjs[propID]),
        };
      }
      return acc;
    }, {}),
);
